import {
  types,
  getEnv,
  flow,
  resolveIdentifier,
  applySnapshot
} from 'mobx-state-tree';
import { autorun } from 'mobx';

import RequestState, { RequestStateType } from '../types/RequestState';
import Section from '../models/Section';
import StatefulStore from '../models/StatefulStore';
import { createErrorModel } from '../util/error';
import SectionStatusType from '../types/SectionStatusType';

const SectionStore = StatefulStore.named('SectionStore')
  .props({
    sections: types.optional(types.array(Section), []),
    activeSection: types.maybeNull(types.reference(Section)),
    singleState: types.optional(RequestStateType, RequestState.LOADED)
  })
  .views((self) => ({
    get sectionIds() {
      return self.sections.map((section) => section.id);
    },
    get activeSections() {
      return self.sections.filter(
        (section) =>
          section.status !== SectionStatusType.TEST_MODE &&
          section.slug.length > 0
      );
    }
  }))
  .actions((self) => {
    return {
      afterAttach() {
        // Update API wrapper sectionId every time it changes
        autorun(() => {
          const sectionId = self.activeSection ? self.activeSection.id : null;
          getEnv(self).apiWrapper.setSectionId(sectionId);
        });
      },
      setup(sections) {
        self.sections = sections;
        self.setLoading(false);
      },
      loadSection: flow(function* loadSection(id) {
        self.singleState = RequestState.LOADING;

        try {
          const section = yield getEnv(self).apiWrapper.request(
            `sections/${id}`
          );
          const sectionModel = self.findSectionById(section.id);
          applySnapshot(sectionModel, section);
        } catch (e) {
          self.singleState = RequestState.ERROR;
          self.lastError = createErrorModel(e);
          throw e;
        }
        self.singleState = RequestState.LOADED;
      }),
      loadSections: flow(function* loadSections() {
        self.setLoading(true);

        try {
          self.sections = yield getEnv(self).apiWrapper.request(`sections`);
        } catch (e) {
          self.setError(e);
          throw e;
        }

        self.setLoading(false);
      }),
      setActiveSection(section) {
        if (self.activeSection !== section) {
          self.activeSection = section;
        }
      },
      getSectionDataForLanguage: flow(function* getSectionDataForLanguage(
        id,
        lang
      ) {
        let sectionData = null;
        try {
          sectionData = yield getEnv(self).apiWrapper.request(
            `sections/${id}`,
            {
              params: {
                lang
              }
            }
          );
        } catch (e) {
          throw e;
        }

        return sectionData;
      }),
      findSectionById(sectionId) {
        return resolveIdentifier(Section, self.sections, sectionId);
      }
    };
  });

export default SectionStore;



// WEBPACK FOOTER //
// ./src/store/SectionStore.js